import { ApiOutlined, BarChartOutlined, CalendarOutlined, CreditCardOutlined, EuroCircleOutlined, HomeOutlined, MailOutlined, MessageOutlined, PhoneOutlined, RobotOutlined } from '@ant-design/icons';
import { IAdmin } from '@leedpulse-monorepo/interfaces';
import { Flex } from 'antd';
import { cloneElement, ReactNode } from 'react';

// Base values only used for creation form
export const EMPTY_ADMIN: IAdmin = {
  id: '',
  createdAt: 0,
  snapshot: false,
  application: {
    key: 'application',
    label: '🏠 Application',
    index: 11,
    app_name: '',
    version: '',
    port: 0,
    debug_mode: false,
    url_leedpulse_api: '',
    timezone: 'Europe/Paris',
  },
  pricing: {
    key: 'pricing',
    label: '💰 Pricing',
    index: 10,
    openai: {
      'gpt-5.1': { input_per_mtok: 0, output_per_mtok: 0 },
      'gpt-5.4-mini': { input_per_mtok: 0, output_per_mtok: 0 },
      'gpt-6.1-sol': { input_per_mtok: 0, output_per_mtok: 0 },
      'gpt-6-luna': { input_per_mtok: 0, output_per_mtok: 0 },
      'whisper-1': { per_minute: 0 },
    },
    elevenlabs: {
      scribe_v2: { per_second: 0 },
    },
  },
  ai: {
    key: 'ai',
    label: '🤖 IA',
    index: 9,
    openai_endpoint_completions: '',
    openai_model_default: '',
    openai_models: {
      LOW: { model: '', reasoningEffort: '' },
      MEDIUM: { model: '', reasoningEffort: '' },
      HIGH: { model: '', reasoningEffort: '' },
      XHIGH: { model: '', reasoningEffort: '' },
    },
    openai_model_transcription: '',
    openai_endpoint_files: '',
    openai_endpoint_finetunes: '',
    openai_endpoint_models: '',
    openai_endpoint_transcriptions: '',
  },
  slack: {
    key: 'slack',
    label: '💬 Slack',
    index: 8,
    slack_webhook_url_leedflow_signup: '',
    slack_webhook_url_leedflow_account_deletion: '',
    slack_webhook_url_leedflow_newsepteoaccount: '',
    slack_webhook_url_leedpulse_feedback_customers: '',
    slack_feedback_channel: '',
    slack_feedback_channel_id: '',
    slack_alerting_channel: '',
  },
  emailing: {
    key: 'emailing',
    label: '📧 Emailing',
    index: 7,
    brevo_api_url: '',
  },
  integrations: {
    key: 'integrations',
    label: '🌐 Integrations CRM',
    index: 6,
    orisha_route_api: '',
    orisha_route_api_recipe: '',
    orisha_client_id: '',
    netty_route_api_v1: '',
    netty_route_api_v2: '',
    api_sirene_url: '',
  },
  workflows: {
    key: 'workflows',
    label: '⚙️ Workflows',
    index: 5,
    workflows: {
      label: 'Workflows contacts',
      custom_field_component: 'WorkflowsSettings',
      'buyer-profile-crm': { tag_ids: [], events: [], delay_minutes: 2, disabled: false },
      'renter-profile-crm': { tag_ids: [], events: [], delay_minutes: 2, disabled: false },
    },
  },
  telephony: {
    key: 'telephony',
    label: '📞 Téléphonie',
    index: 4,
    callr_route_api: '',
    twilio_whatsapp_test_sender: '',
    twilio_whatsapp_test_receiver: '',
    twilio_whatsapp_status_callback: '',
    whatsapp_api_base_url: '',
  },
  stripe: {
    key: 'stripe',
    label: '💳 Stripe',
    index: 3,
    stripe_session_status_url: '',
    stripe_dashboard_url: '',
  },
  google_calendar: {
    key: 'google_calendar',
    label: '📅 Google Calendar',
    index: 2,
    google_calendar_endpoint_calendar: '',
    google_calendar_endpoint_events: '',
    google_calendar_endpoint_email: '',
    google_calendar_redirect_uri: '',
    google_calendar_client_id: '',
  },
  sentry: {
    key: 'sentry',
    label: '📊 Sentry',
    index: 1,
    sentry_api_url: '',
    sentry_organization_slug: '',
    sentry_projects: [],
  },
};

const BASE_ADMIN_EMOJIS_TO_ANTD_ICONS = {
  '🏠': <HomeOutlined />,
  '💰': <EuroCircleOutlined />,
  '🤖': <RobotOutlined />,
  '💬': <MessageOutlined />,
  '📧': <MailOutlined />,
  '🌐': <ApiOutlined />,
  '📞': <PhoneOutlined />,
  '💳': <CreditCardOutlined />,
  '📅': <CalendarOutlined />,
  '📊': <BarChartOutlined />,
};

/**
 * Replace emojis in the label by the related React icon (when available).
 */
export const getAdminTabLabel = (label: string): ReactNode => {
  const nodes: ReactNode[] = [];
  const emojiRegex = /\p{Emoji_Presentation}/u;

  // Iterate over unicode code points (safer than indexing a string).
  for (const char of label) {
    if (!emojiRegex.test(char)) {
      nodes.push(char);
      continue;
    }

    let iconOrChar: ReactNode = char;

    if (char in BASE_ADMIN_EMOJIS_TO_ANTD_ICONS) {
      iconOrChar = cloneElement(BASE_ADMIN_EMOJIS_TO_ANTD_ICONS[char as keyof typeof BASE_ADMIN_EMOJIS_TO_ANTD_ICONS], { style: { marginRight: 6 } });
    }

    nodes.push(iconOrChar);
  }

  return (
    <Flex align="center">
      {nodes.map((node, idx) => (
        <span key={idx}>{node}</span>
      ))}
    </Flex>
  );
};